import appConfigs from "@configs/app";
import DeviceUtils from "@utils/device.utils";
import { appLocalStorage } from "@utils/app.storage";
import store from "@plugins/store";
import { computed } from "vue";

const userId = computed(() => store.getters["auth/userId"]);
const TINYMCE_TOOLBAR_COLLAPSE_KEY = "tinymce-toolbar-collapsed";

function getStorageKey(): string {
  return `${TINYMCE_TOOLBAR_COLLAPSE_KEY}-${userId.value}`;
}

/** Returns true if toolbar should be collapsed (default: true). */
export function getToolbarCollapsed(): boolean {
  const stored = appLocalStorage.getItem(getStorageKey());
  if (stored === null) return true;
  return stored === "true";
}

/** Persist toolbar collapse preference. */
export function setToolbarCollapsed(collapsed: boolean): void {
  appLocalStorage.setItem(getStorageKey(), collapsed);
}

// Set active editor to dummy editor on document click to hide the toolbar
// when user clicks outside of the editor after focused on editor.
document.addEventListener("click", function (event) {
  // On some mobile browsers editor blur event triggered after document click event
  // and blurEditor is not set if document click event triggered first.
  // We use set timeout to enable blur event handler to set blurEditor.
  if (window.tinymce) {
    window.tinymce.clickedElement = event.target;
  }
  setTimeout(function waitEditorBlurEvent() {
    if (!window.tinymce || !window.tinymce.blurEditor) return;
    if (window.tinymce.blurEditor === window.tinymce.activeEditor) {
      // Intentionally left both settings property and fire function implementation empty
      // because setActive calls fire function and access settings property of the dummyEditor
      // and exception will throw either fire function or settings property is missing.
      const dummyEditor = {
        settings: {},
        dispatch: function () {
          // Dispatch implementation - intentionally empty
        },
        isDirty: function () {
          return false;
        },
      };
      tinymce.setActive(dummyEditor);
      delete tinymce.blurEditor;
    }
  }, 100);
});

// On some mobile browsers editor blur event triggered after save/submit button click event
// and editor could not save the content before save/submit button click event triggered.
// We force editor to save content on document click if it is dirty using event capturing.
document.addEventListener(
  "click",
  function () {
    if (!window.tinymce || !window.tinymce.activeEditor) return;
    if (window.tinymce.activeEditor.isDirty()) {
      window.tinymce.activeEditor.save();
    }
  },
  true,
);

const allowedContextMenuItems = [
  "undo",
  "redo",
  "cut",
  "copy",
  "paste",
  "bold",
  "italic",
  "underline",
  "other_formatting",
  "numlist",
  "bullist",
];

// Generate context menu from toolbar items
function generateContextMenu(toolbar: string | string[]): string {
  const flattenToolbar = Array.isArray(toolbar) ? toolbar.join(" ") : toolbar;
  const toolbarItems = flattenToolbar.split(/\s+/).filter((item) => item !== "|" && item !== "");
  const contextItems = allowedContextMenuItems.filter((item) => toolbarItems.includes(item));

  const groups = [
    { items: ["undo", "redo"], label: "undo redo", requireAll: true }, // Undo Redo
    { items: ["cut", "copy", "paste"], label: "cut copy paste", requireAll: true }, // Cut Copy Paste
    {
      items: ["bold", "italic", "underline", "other_formatting"],
      label: "styles",
      requireAll: false,
    }, // Text formatting
    { items: ["numlist", "bullist"], label: "lists", requireAll: false }, // List operations
  ];

  const formattedMenu = groups
    .filter((group) =>
      group.requireAll
        ? group.items.every((item) => contextItems.includes(item))
        : group.items.some((item) => contextItems.includes(item)),
    )
    .map((group) => group.label)
    .join(" | ");

  return formattedMenu || "false";
}

interface mobileConfig {
  toolbar_mode: string;
  plugins: string;
  toolbar: string | string[];
  contextmenu?: string | boolean;
}

interface pluginErrorConfig {
  errorMsg: string;
}

interface styleFormatItem {
  title: string;
  format: string;
}

interface styleFormat {
  title: string;
  items: styleFormatItem[];
}

interface tinyMceConfig {
  plugins: string;
  toolbar: string | string[];
  contextmenu?: string | boolean;
  mobile?: mobileConfig;
  isMissingPluginElement?: Record<string, pluginErrorConfig>;
  style_formats?: styleFormat[];
}

function autoInjectContextMenu(config: tinyMceConfig): tinyMceConfig {
  const newConfig = { ...config };

  // For desktop: auto-generate context menu from toolbar
  if (config.toolbar) {
    newConfig.contextmenu = generateContextMenu(config.toolbar);
  }

  // For mobile: disable context menu completely
  if (DeviceUtils.isMobile()) {
    newConfig.contextmenu = false;

    if (newConfig.mobile) {
      newConfig.mobile.contextmenu = false;
    }
  }

  return newConfig;
}

export default {
  licenseKey: appConfigs.tinymceLicenseKey,
  scriptSrc: `${appConfigs.vleDomain}/plugins/@tinymce/tinymce/tinymce.min.js`,
  base: {
    convert_unsafe_embeds: true,
    allow_html_in_named_anchor: true,
    branding: false,
    custom_ui_selector: ".mce-btn button",
    color_default_background: "#FFFF00",
    contextmenu: "false",
    contextmenu_never_use_native: false,
    entity_encoding: "raw",
    elementpath: false,
    extended_valid_elements: "*[.*],a[*],#span[*],#div[*],sls-frame[*],#svg[*],#path[*],#math[*]",
    extended_mathml_attributes: ["linebreak"],
    valid_children: "+a[div],+div[sls-frame]",
    custom_elements: "sls-frame",
    forced_root_block: "div",
    forced_root_block_attrs: {
      class: "paragraph",
    },
    invalid_elements: "object",
    height: 200,
    menubar: false,
    font_family_formats: `Noto Sans=Noto Sans,Arial,sans-serif;English - Roman=Noto Serif,Times New Roman,Times,serif;Tamil=Noto Sans Tamil,Noto Sans,Arial,serif;中 - 宋=Noto Serif SC,STSong,SimSun,宋体,Arial,serif;中 - 楷=STKaiTi,KaiTi,楷体,WenQuanYi Micro Hei,UKai,Arial,serif;中 - 黑=Noto Sans SC,Hiragino Sans GB,STHeiti,Heiti SC,微软雅黑,Microsoft Yahei,Arial,serif;Anjal New InaiMathi Sch Book=Anjal New InaiMathi Sch Book,serif;Andika=Andika,sans-serif;Bitter=Bitter,serif;EB Garamond=EB Garamond,serif;Fira Sans=Fira Sans,sans-serif;Fontdiner Swanky=Fontdiner Swanky,serif;Gaegu=Gaegu,sans-serif;Josefin Slab=Josefin Slab,serif;Kavoon=Kavoon,serif;Merriweather=Merriweather,serif;Montserrat=Montserrat,sans-serif;Nunito=Nunito,sans-serif;Quicksand=Quicksand,sans-serif;Source Code Pro=Source Code Pro,monospace;Staatliches=Staatliches,sans-serif;`,
    smart_paste: false,
    paste_block_drop: true,
    relative_urls: false,
    inline: true,
    toolbar_mode: "sliding",
    /* Latest wiris plugin version is not compatible with sls-web-wiris server-side.
     * Need to update sls-web-wiris server-side to use the latest wiris jars.
     */
    external_plugins: {
      tiny_mce_wiris: `${appConfigs.vleDomain}/plugins/@wiris/mathtype-tinymce7/plugin.min.js`,
      licensekeymanager: `${appConfigs.vleDomain}/plugins/@tinymce/tinymce-premium/plugins/licensekeymanager/plugin.min.js`,
    },
    mathTypeParameters: {
      editorParameters: {
        centerBaseline: "false",
        fontSize: "16px",
        detectHand: "true",
      },
    },
    table_clone_elements: "strong em b i span font h1 h2 h3 h4 h5 h6 p",
    style_formats: [
      {
        title: "Blocks",
        items: [
          { title: "Paragraph", format: "p" },
          { title: "Heading 1", format: "h1" },
          { title: "Heading 2", format: "h2" },
          { title: "Heading 3", format: "h3" },
          { title: "Heading 4", format: "h4" },
          { title: "Heading 5", format: "h5" },
          { title: "Heading 6", format: "h6" },
          { title: "Preformatted", format: "pre" },
        ],
      },
      {
        title: "Inline",
        items: [
          { title: "Bold", format: "bold" },
          { title: "Italic", format: "italic" },
          { title: "Underline", format: "underline" },
          { title: "Strikethrough", format: "strikethrough" },
          { title: "Subscript", format: "subscript" },
          { title: "Superscript", format: "superscript" },
        ],
      },
      {
        title: "Align",
        items: [
          { title: "Left", format: "alignleft" },
          { title: "Center", format: "aligncenter" },
          { title: "Right", format: "alignright" },
          { title: "Justify", format: "alignjustify" },
        ],
      },
    ],
    text_patterns: false,
    // link plugin default target value
    link_default_target: "_blank",
    selector: "#editor",
    content_style:
      ".mymention{ color: gray; }" +
      ".mymention[data-teacher]{ color: red; }" +
      "sup, sub { font-size: 65%; }",
    font_size_formats: "8pt 10pt 12pt 14pt 18pt 24pt 36pt",
    line_height_formats: "1 1.15 1.5 2 2.5 3",
    mobile: {
      toolbar_mode: "sliding",
    },
  },
  basic: autoInjectContextMenu({
    plugins: "autolink alignment external_media advlist lists table tiny_mce_wiris",
    toolbar:
      "undo redo | bold italic | fontfamily | blocks forecolor backcolor alignment numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry table cut copy paste pastetext",
  }),
  teacher_tinymce_1: autoInjectContextMenu({
    // used by Teacher, full descriptive
    plugins:
      "autolink external_media advlist lists table link dynamic_link alignment drawing stp text_to_speech tooltip media_object acp_image embed_website youtube tiny_mce_wiris emoticons character_word_count other_formatting",
    toolbar:
      "undo redo | bold italic underline | other_formatting | fontfamily fontsize | blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry table cut copy paste pastetext emoticons media_object acp_image drawing youtube dynamic_link embed_website tooltip text_to_speech",
  }),
  teacher_tinymce_1_gamification: autoInjectContextMenu({
    // used by Teacher, full descriptive
    plugins:
      "autolink external_media advlist lists table link dynamic_link alignment drawing stp text_to_speech tooltip media_object embed_website youtube tiny_mce_wiris emoticons character_word_count other_formatting",
    toolbar:
      "undo redo | bold italic underline | other_formatting | fontfamily fontsize | blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry table cut copy paste pastetext emoticons media_object drawing youtube dynamic_link embed_website tooltip text_to_speech",
    mobile: {
      toolbar_mode: "sliding",
      plugins:
        "autolink external_media advlist lists table link alignment drawing stp text_to_speech tooltip media_object embed_website youtube tiny_mce_wiris emoticons character_word_count",
      toolbar:
        "undo redo | bold italic underline | other_formatting | fontfamily fontsize | blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry table pastetext emoticons media_object drawing youtube dynamic_link embed_website tooltip text_to_speech",
    },
  }),
  teacher_tinymce_2: autoInjectContextMenu({
    // used by Teacher, full descriptive for FIB
    plugins:
      "autolink external_media advlist lists table link dynamic_link alignment drawing stp text_to_speech tooltip student_tooltip media_object acp_all embed_website youtube tiny_mce_wiris fib_blank emoticons character_word_count other_formatting",
    toolbar:
      "fib_blank | undo redo | bold italic underline | other_formatting fontfamily | fontsize blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry table cut copy paste pastetext emoticons media_object acp_all drawing youtube dynamic_link embed_website tooltip text_to_speech",
    isMissingPluginElement: {
      fib_blank: {
        errorMsg: "question_component_error_fill_at_least_one_blank",
      },
    },
  }),
  teacher_tinymce_4: autoInjectContextMenu({
    // used by Teacher, Announcement message
    plugins:
      "autolink external_media advlist alignment lists table link mylink tooltip tiny_mce_wiris emoticons other_formatting",
    toolbar:
      "undo redo | bold italic underline | other_formatting | fontfamily fontsize | blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry table cut copy paste pastetext emoticons mylink tooltip",
  }),
  teacher_tinymce_5: autoInjectContextMenu({
    // used by Teacher, confined description (rubrics table)
    plugins:
      "autolink external_media advlist lists tiny_mce_wiris media_object emoticons other_formatting",
    toolbar:
      "undo redo | bold italic underline | other_formatting | numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry cut copy paste pastetext emoticons media_object",
    style_formats: [
      {
        title: "Inline",
        items: [
          { title: "Bold", format: "bold" },
          { title: "Italic", format: "italic" },
          { title: "Underline", format: "underline" },
          { title: "Strikethrough", format: "strikethrough" },
          { title: "Subscript", format: "subscript" },
          { title: "Superscript", format: "superscript" },
        ],
      },
    ],
  }),
  teacher_tinymce_6: autoInjectContextMenu({
    // used by Teacher, EEQ, App Feature Prompt
    plugins: "autolink character_word_count other_formatting",
    toolbar: "undo redo | bold italic underline | other_formatting cut copy paste pastetext",
    style_formats: [
      {
        title: "Inline",
        items: [
          { title: "Bold", format: "bold" },
          { title: "Italic", format: "italic" },
          { title: "Underline", format: "underline" },
          { title: "Strikethrough", format: "strikethrough" },
          { title: "Subscript", format: "subscript" },
          { title: "Superscript", format: "superscript" },
        ],
      },
    ],
  }),
  teacher_tinymce_7: autoInjectContextMenu({
    // used by Teacher, Pre-populated Student Response
    plugins:
      "autolink external_media advlist alignment lists table link mylink tooltip tiny_mce_wiris media_object acp_all google drawing emoticons text_to_speech character_word_count other_formatting",
    toolbar:
      "undo redo | bold italic underline | other_formatting | fontfamily fontsize | blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry table cut copy paste pastetext emoticons media_object acp_all google drawing mylink tooltip text_to_speech",
  }),
  teacher_tinymce_8: autoInjectContextMenu({
    // used by Teacher, Audio Response Edit with Speech Evaluation enabled
    plugins:
      "autolink external_media advlist lists table link dynamic_link alignment drawing stp tooltip student_tooltip media_object acp_image embed_website youtube tiny_mce_wiris emoticons text_to_speech speech_evaluation character_word_count other_formatting",
    toolbar:
      "undo redo | bold italic underline | other_formatting | fontfamily fontsize | blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry table cut copy paste pastetext emoticons media_object acp_image drawing youtube dynamic_link embed_website tooltip text_to_speech speech_evaluation",
    isMissingPluginElement: {
      speech_evaluation: {
        errorMsg: "question_component_error_missing_speech_evaluation_component",
      },
    },
  }),
  teacher_tinymce_9: autoInjectContextMenu({
    // used by Teacher, Pre-populated Student Response with AK enabled
    plugins:
      "autolink external_media advlist alignment lists table link mylink tooltip tiny_mce_wiris media_object acp_image google drawing emoticons algebra_kit character_word_count other_formatting",
    toolbar:
      "undo redo | bold italic underline | other_formatting | fontfamily fontsize | blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry algebra_kit table cut copy paste pastetext emoticons media_object acp_image google drawing mylink tooltip",
  }),
  teacher_tinymce_10: autoInjectContextMenu({
    // used in text component only (disabled app for FA Mathematics)
    plugins:
      "autolink external_media advlist lists table link dynamic_link alignment drawing student_tooltip stp text_to_speech tooltip media_object acp_all google embed_website youtube tiny_mce_wiris emoticons character_word_count algebra_kit other_formatting",
    toolbar:
      "undo redo | bold italic underline | other_formatting | fontfamily fontsize | blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry table cut copy paste pastetext emoticons media_object acp_all google drawing youtube dynamic_link embed_website tooltip text_to_speech",
  }),
  teacher_tinymce_11: autoInjectContextMenu({
    // used by Teacher, Teacher Feedback
    plugins:
      "custom_mentions lists tiny_mce_wiris drawing recording file_upload external_media acp_image google character_word_count",
    toolbar:
      "undo redo | numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry cut copy paste drawing file_upload acp_image google recording",
  }),
  teacher_tinymce_12: autoInjectContextMenu({
    // used by Teacher, various question types
    plugins:
      "autolink external_media advlist lists table link dynamic_link alignment drawing stp tooltip student_tooltip media_object acp_all embed_website youtube tiny_mce_wiris emoticons text_to_speech character_word_count algebra_kit other_formatting",
    toolbar:
      "undo redo | bold italic underline | other_formatting | fontfamily fontsize | blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry table cut copy paste pastetext emoticons media_object acp_all drawing youtube dynamic_link embed_website tooltip text_to_speech",
  }),
  teacher_tinymce_13: autoInjectContextMenu({
    // used by Teacher, Pre-populated Student Response for ITT only
    plugins:
      "autolink external_media advlist alignment lists table link mylink tooltip tiny_mce_wiris media_object acp_image drawing emoticons text_to_speech character_word_count other_formatting",
    toolbar:
      "undo redo | bold italic underline | other_formatting | fontfamily fontsize | blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry table cut copy paste pastetext emoticons media_object acp_image drawing mylink tooltip text_to_speech",
  }),
  teacher_tinymce_14: autoInjectContextMenu({
    // used by Teacher, various ITT/POLL body
    plugins:
      "autolink external_media advlist lists table link dynamic_link alignment acp_image drawing stp tooltip media_object embed_website youtube tiny_mce_wiris emoticons text_to_speech character_word_count other_formatting",
    toolbar:
      "undo redo | bold italic underline | other_formatting | fontfamily fontsize | blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry table cut copy paste pastetext emoticons media_object acp_image drawing youtube dynamic_link embed_website tooltip text_to_speech",
  }),
  teacher_tinymce_15: autoInjectContextMenu({
    // used by Teacher, Lesson Notes
    plugins:
      "autolink external_media advlist alignment lists table link mylink tooltip tiny_mce_wiris emoticons character_word_count other_formatting",
    toolbar:
      "undo redo | bold italic underline | other_formatting | fontfamily fontsize | blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry table cut copy paste pastetext emoticons mylink tooltip",
    mobile: {
      toolbar_mode: "sliding",
      plugins:
        "autolink external_media advlist lists table link mylink alignment tooltip tiny_mce_wiris emoticons other_formatting",
      toolbar:
        "undo redo | bold italic underline | other_formatting | fontfamily fontsize | blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry table pastetext emoticons mylink tooltip",
    },
  }),
  teacher_tinymce_16: autoInjectContextMenu({
    // used by Teacher, Pre-populated Student Response with HTML5_Interactive
    plugins:
      "autolink external_media advlist alignment lists table link mylink tooltip tiny_mce_wiris media_object google drawing emoticons text_to_speech character_word_count html5_interactive other_formatting",
    toolbar:
      "undo redo | bold italic underline | other_formatting | fontfamily fontsize | blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry table cut copy paste pastetext emoticons media_object google drawing mylink tooltip text_to_speech html5_interactive",
    isMissingPluginElement: {
      html5_interactive: {
        errorMsg: "question_component_error_missing_html5_interactive_component",
      },
    },
  }),
  teacher_tinymce_17: autoInjectContextMenu({
    // used by Knowledge Base
    plugins: "autolink media_object google drawing character_word_count",
    toolbar: "undo redo cut copy paste pastetext media_object google drawing",
  }),
  teacher_tinymce_18: autoInjectContextMenu({
    // used in text component with AK
    plugins:
      "autolink external_media advlist lists table link dynamic_link alignment drawing student_tooltip stp text_to_speech tooltip media_object acp_all google embed_website youtube tiny_mce_wiris emoticons character_word_count algebra_kit other_formatting",
    toolbar:
      "undo redo | bold italic underline other_formatting | fontfamily fontsize | blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry algebra_kit table cut copy paste pastetext emoticons media_object acp_all google drawing youtube dynamic_link embed_website tooltip text_to_speech",
  }),
  teacher_tinymce_19: autoInjectContextMenu({
    // used by Teacher, various question types with AK
    plugins:
      "autolink external_media advlist lists table link dynamic_link alignment drawing stp tooltip student_tooltip media_object acp_all embed_website youtube tiny_mce_wiris emoticons text_to_speech character_word_count algebra_kit other_formatting",
    toolbar:
      "undo redo | bold italic underline | other_formatting | fontfamily fontsize | blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry algebra_kit table cut copy paste pastetext emoticons media_object acp_all drawing youtube dynamic_link embed_website tooltip text_to_speech",
  }),
  teacher_tinymce_21: autoInjectContextMenu({
    // used by Teacher, ISR scorable response (HTML5 interactive + Embed App only)
    plugins: "html5_interactive embed_website external_media",
    toolbar: "html5_interactive embed_website",
  }),
  teacher_tinymce_20: {
    // DAT teacher save analysis
    plugins:
      "tiny_mce_wiris drawing file_upload external_media recording lists character_word_count",
    toolbar:
      "numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry drawing file_upload recording",
  },
  student_tinymce_1: autoInjectContextMenu({
    // used by Student attempt, FREE response question
    // putting google in plugins only but not toolbar to render Google Attachment for FRQ,
    // but don't allow student to attach any Google Attachment (this is only allowed in FSQ)
    // add same handling above for file_upload, to render file attached by teacher in prepopulated student answer
    plugins:
      "google autolink external_media advlist alignment lists table link mylink drawing tooltip file_upload tiny_mce_wiris emoticons text_to_speech character_word_count other_formatting",
    toolbar:
      "undo redo | bold italic underline | other_formatting | fontfamily fontsize | blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry table cut copy paste pastetext emoticons drawing mylink tooltip",
  }),
  student_tinymce_2: autoInjectContextMenu({
    // used by Student attempt, FILE submission question
    plugins:
      "google autolink external_media advlist alignment lists table link mylink drawing tooltip file_upload tiny_mce_wiris emoticons character_word_count other_formatting",
    toolbar:
      "file_upload google | undo redo | bold italic underline | other_formatting | fontfamily fontsize | blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry table cut copy paste pastetext emoticons drawing mylink tooltip",
  }),
  student_tinymce_3: {
    // used by Student attempt, Audio Response Question
    plugins: "recording file_upload external_media character_word_count",
    toolbar: "recording file_upload",
  },
  student_tinymce_4: {
    // used by Student attempt, SE Audio Response Question
    plugins: "recording external_media character_word_count",
    toolbar: "recording",
  },
  student_tinymce_5: autoInjectContextMenu({
    // used by Student attempt, FILE submission question in MOE
    plugins:
      "autolink external_media advlist alignment lists table link mylink drawing tooltip file_upload tiny_mce_wiris emoticons character_word_count other_formatting",
    toolbar:
      "file_upload | undo redo | bold italic underline | other_formatting | fontfamily fontsize | blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry table cut copy paste pastetext emoticons drawing mylink tooltip",
  }),
  student_tinymce_6: autoInjectContextMenu({
    // used by notebook note and cover
    plugins:
      "recording autolink external_media advlist alignment lists table link notebook_dynamic_link drawing tooltip file_upload tiny_mce_wiris emoticons other_formatting character_word_count google",
    toolbar:
      "undo redo | bold italic underline | other_formatting | fontfamily fontsize | blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry table cut copy paste pastetext emoticons file_upload google drawing notebook_dynamic_link tooltip recording",
    mobile: {
      toolbar_mode: "sliding",
      plugins:
        "recording autolink external_media advlist lists table link notebook_dynamic_link alignment drawing tooltip file_upload tiny_mce_wiris emoticons other_formatting character_word_count google",
      toolbar:
        "undo redo | bold italic underline | other_formatting | fontfamily fontsize | blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry table pastetext emoticons file_upload google drawing notebook_dynamic_link tooltip recording",
    },
  }),
  student_teacher_tinymce_1: {
    plugins: "emoticons tiny_mce_wiris drawing external_media file_upload algebra_kit",
    toolbar:
      "tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry emoticons drawing file_upload",
  },
  student_teacher_tinymce_2: {
    // basic comments
    plugins: "custom_mentions tiny_mce_wiris character_word_count",
    toolbar: "tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry",
  },
  student_teacher_tinymce_3: {
    // do not remove lists plugins. To cater backspace behavior when it is added from teacher's side, but not showing in student's toolbar
    plugins:
      "tiny_mce_wiris drawing file_upload external_media recording lists character_word_count media_object",
    toolbar:
      "numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry drawing file_upload recording",
  },
  student_teacher_tinymce_4: autoInjectContextMenu({
    // aggregated student response teacher feedback
    plugins:
      "custom_mentions lists tiny_mce_wiris drawing recording file_upload external_media google character_word_count emoticons",
    toolbar:
      "undo redo numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry | cut copy paste pastetext emoticons drawing file_upload google recording",
  }),
  // Annotation
  student_teacher_tinymce_5: autoInjectContextMenu({
    plugins:
      "custom_mentions emoticons drawing external_media alignment file_upload algebra_kit recording table lists character_word_count other_formatting tiny_mce_wiris",
    toolbar:
      "undo redo | bold italic underline | other_formatting | fontfamily fontsize blocks forecolor backcolor alignment lineheight numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry table cut copy paste pastetext emoticons file_upload drawing recording",
    style_formats: [
      {
        title: "Inline",
        items: [
          { title: "Bold", format: "bold" },
          { title: "Italic", format: "italic" },
          { title: "Underline", format: "underline" },
          { title: "Strikethrough", format: "strikethrough" },
          { title: "Subscript", format: "subscript" },
          { title: "Superscript", format: "superscript" },
        ],
      },
    ],
  }),
  // Post
  student_teacher_tinymce_6: autoInjectContextMenu({
    plugins:
      "custom_mentions tiny_mce_wiris drawing file_upload external_media recording lists character_word_count",
    toolbar:
      "numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry drawing file_upload recording",
  }),
  student_teacher_tinymce_7: autoInjectContextMenu({
    plugins:
      "tiny_mce_wiris drawing file_upload external_media recording lists character_word_count",
    toolbar:
      "numlist bullist | tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry | drawing file_upload recording",
    mobile: {
      toolbar_mode: "sliding",
      plugins:
        "tiny_mce_wiris drawing file_upload external_media recording lists character_word_count",
      toolbar:
        "numlist bullist | tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry | drawing file_upload recording",
    },
  }),
  student_teacher_tinymce_8: autoInjectContextMenu({
    plugins:
      "custom_mentions tiny_mce_wiris emoticons drawing file_upload external_media recording lists character_word_count",
    toolbar:
      "tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry emoticons drawing file_upload recording",
    mobile: {
      toolbar_mode: "sliding",
      plugins:
        "custom_mentions tiny_mce_wiris emoticons drawing file_upload external_media recording lists character_word_count",
      toolbar:
        "tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry emoticons drawing file_upload recording",
    },
  }),
  content_officer_approver_tinymce_1: autoInjectContextMenu({
    // used by Content Map Editor
    plugins: "autolink advlist lists tiny_mce_wiris emoticons link other_formatting",
    toolbar:
      "undo redo | bold italic underline | other_formatting forecolor backcolor numlist bullist tiny_mce_wiris_formulaEditor tiny_mce_wiris_formulaEditorChemistry cut copy paste pastetext emoticons link",
    style_formats: [
      {
        title: "Inline",
        items: [
          { title: "Bold", format: "bold" },
          { title: "Italic", format: "italic" },
          { title: "Underline", format: "underline" },
          { title: "Strikethrough", format: "strikethrough" },
          { title: "Subscript", format: "subscript" },
          { title: "Superscript", format: "superscript" },
        ],
      },
    ],
  }),
  plaintext: {
    plugins: "plaintext character_word_count",
    toolbar: "",
    paste_as_text: true,
  },
  size: {
    extra_small: {
      character_limit: 50,
    },
    small: {
      character_limit: 250,
    },
    medium: {
      character_limit: 500,
    },
    large: {
      character_limit: 2000,
    },
    extra_large: {
      character_limit: 30000,
    },
    super_large: {
      character_limit: 100000,
    },
  },
  disable_shortcut_patterns: [
    // "meta" maps to Command on Mac and Ctrl on PC
    "meta+b",
    "meta+i",
    "meta+u",
    // "access" maps to Control+Option on Mac and shift+alt on PC
    "access+1",
    "access+2",
    "access+3",
    "access+4",
    "access+5",
    "access+6",
    "access+7",
    "access+8",
    "access+9",
  ],
};
